import { useTranslate } from '@tolgee/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/components/ui/Button';
import { fetchBackend } from '~/helper/fetchBackend';
import { useAppParams } from '~/helper/useAppParams';
import { trackEvent } from '@agentselly/analytics';

interface AvailabilitySlot {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly label: string;
  readonly agentType?: 'internal' | 'external';
  readonly booked?: boolean;
  readonly advisor?: {
    readonly name?: string;
    readonly photoUrl?: string;
    readonly jobTitle?: string;
    readonly team?: string;
  };
  readonly travelSeconds?: number;
  readonly anfahrtStart?: string;
  readonly anfahrtEnd?: string;
  readonly rueckfahrtStart?: string;
  readonly rueckfahrtEnd?: string;
  readonly address?: string;
}

interface AdvisorInfo {
  readonly name: string;
  readonly photoUrl?: string;
  readonly jobTitle?: string;
}

interface AvailabilityResponse {
  readonly propertyId: string;
  readonly duration: number;
  readonly timezone: string;
  readonly address?: string;
  readonly advisor?: AdvisorInfo;
  readonly slots: readonly AvailabilitySlot[];
  readonly fallback: 'slots' | 'requestOnly';
  readonly message?: string;
}

interface PrefillContact {
  readonly salutation?: '0' | '1' | null;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly street?: string;
  readonly no?: string;
  readonly zip?: string;
  readonly city?: string;
  readonly isLoggedIn?: boolean;
}

interface Props {
  readonly params?: {
    readonly lang: string;
    readonly companyId: string;
    readonly contactId: string;
    readonly propertyId: string;
  };
  readonly availability?: AvailabilityResponse;
  readonly contact?: PrefillContact;
}

interface DayGroup {
  readonly dateKey: string;
  readonly dateLabel: string;
  readonly shortWeekday: string;
  readonly dayNum: string;
  readonly shortMonth: string;
  readonly slots: readonly AvailabilitySlot[];
}

const ZURICH_TZ = 'Europe/Zurich';

function groupSlotsByDay(slots: readonly AvailabilitySlot[]): DayGroup[] {
  const map = new Map<string, AvailabilitySlot[]>();
  for (const slot of slots) {
    const date = new Date(slot.start);
    const dateKey = new Intl.DateTimeFormat('de-CH', {
      timeZone: ZURICH_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
    const existing = map.get(dateKey);
    if (existing) existing.push(slot);
    else map.set(dateKey, [slot]);
  }
  return Array.from(map.entries()).map(
    ([
      dateKey,
      daySlots,
    ]) => {
      const first = new Date(daySlots[0].start);
      const dateLabel = new Intl.DateTimeFormat('de-CH', {
        timeZone: ZURICH_TZ,
        weekday: 'long',
        day: '2-digit',
        month: 'long',
      }).format(first);
      const shortWeekday = new Intl.DateTimeFormat('de-CH', {
        timeZone: ZURICH_TZ,
        weekday: 'short',
      }).format(first);
      const dayNum = new Intl.DateTimeFormat('de-CH', {
        timeZone: ZURICH_TZ,
        day: '2-digit',
      }).format(first);
      const shortMonth = new Intl.DateTimeFormat('de-CH', {
        timeZone: ZURICH_TZ,
        month: 'short',
      }).format(first);
      return {
        dateKey,
        dateLabel,
        shortWeekday,
        dayNum,
        shortMonth,
        slots: daySlots,
      };
    },
  );
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat('de-CH', {
    timeZone: ZURICH_TZ,
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function formatDateTimeLong(iso: string): string | null {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return new Intl.DateTimeFormat('de-CH', {
      timeZone: ZURICH_TZ,
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return null;
  }
}

function initials(name?: string): string {
  const parts = (name || '')
    .split(' ')
    .map((part) => part.trim())
    .filter(Boolean);
  return (parts[0]?.[0] || 'A') + (parts[1]?.[0] || 'S');
}

function CalendarIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6.75 3v2.25M17.25 3v2.25M3.75 8.25h16.5M5.25 5.25h13.5a1.5 1.5 0 011.5 1.5v12a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-12a1.5 1.5 0 011.5-1.5z"
      />
    </svg>
  );
}

function HomeIcon() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.8}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 10.5L12 3l9 7.5M5.25 9.75v9A1.25 1.25 0 006.5 20h11a1.25 1.25 0 001.25-1.25v-9M9.75 20v-5.25h4.5V20"
      />
    </svg>
  );
}

function HomeTypeRadio({
  value,
  selected,
  onChange,
  label,
}: {
  readonly value: 'owning' | 'renting';
  readonly selected: boolean;
  readonly onChange: (v: 'owning' | 'renting') => void;
  readonly label: string;
}) {
  return (
    <label
      className={`flex min-h-[44px] flex-1 md:flex-none cursor-pointer items-center gap-2 rounded-xl border px-3 transition text-sm ${
        selected
          ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 font-medium'
          : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
      }`}
    >
      <input
        type="radio"
        name="homeType"
        checked={selected}
        onChange={() => onChange(value)}
        className="h-4 w-4 text-gray-900 dark:text-white focus-visible:ring-gray-900 dark:focus-visible:ring-white"
      />
      {label}
    </label>
  );
}

function StepBadge({
  children,
  active,
}: {
  readonly children: React.ReactNode;
  readonly active: boolean;
}) {
  return (
    <span
      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-colors ${
        active
          ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-950'
          : 'bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400'
      }`}
    >
      {children}
    </span>
  );
}

export function VisitBookingWidget(props: Props) {
  const { t } = useTranslate();
  const routeParams = useAppParams();
  const params = props.params || routeParams;
  const propertyId = params?.propertyId ?? '';

  const [
    availability,
    setAvailability,
  ] = useState<AvailabilityResponse | null>(props.availability ?? null);
  const [
    loadingAvailability,
    setLoadingAvailability,
  ] = useState(!props.availability);
  const [
    availabilityError,
    setAvailabilityError,
  ] = useState<string | null>(null);

  const [
    selectedSlot,
    setSelectedSlot,
  ] = useState<AvailabilitySlot | null>(null);

  const contact = props.contact;
  const [
    salutation,
    setSalutation,
  ] = useState<'0' | '1'>(
    contact?.salutation === '0' || contact?.salutation === '1'
      ? contact.salutation
      : '1',
  );
  const [
    firstName,
    setFirstName,
  ] = useState(contact?.firstName ?? '');
  const [
    lastName,
    setLastName,
  ] = useState(contact?.lastName ?? '');
  const [
    email,
    setEmail,
  ] = useState(contact?.email ?? '');
  const [
    phone,
    setPhone,
  ] = useState(contact?.phone ?? '');
  const [
    street,
    setStreet,
  ] = useState(contact?.street ?? '');
  const [
    no,
    setNo,
  ] = useState(contact?.no ?? '');
  const [
    zip,
    setZip,
  ] = useState(contact?.zip ?? '');
  const [
    city,
    setCity,
  ] = useState(contact?.city ?? '');
  const [
    homeType,
    setHomeType,
  ] = useState<'owning' | 'renting' | ''>('');
  const [
    message,
    setMessage,
  ] = useState('');
  const [
    consent,
    setConsent,
  ] = useState(false);
  const [
    hasSubmitted,
    setHasSubmitted,
  ] = useState(false);
  const [
    submitting,
    setSubmitting,
  ] = useState(false);
  const [
    submitError,
    setSubmitError,
  ] = useState<string | null>(null);
  const [
    alreadyBooked,
    setAlreadyBooked,
  ] = useState<{
    readonly existingStart?: string | null;
    readonly contactEmail?: string;
  } | null>(null);
  const [
    success,
    setSuccess,
  ] = useState<{
    readonly status: 'booked' | 'requested';
    readonly slotStart?: string;
    readonly dossierId?: string;
    readonly mongoPropertyId?: string;
    readonly contactId?: string;
  } | null>(null);
  const [
    touched,
    setTouched,
  ] = useState<Record<string, boolean>>({});

  function touch(field: string) {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }

  const isLoggedIn = Boolean(contact?.isLoggedIn && contact?.email);
  const emailLocked = isLoggedIn;

  useEffect(() => {
    if (props.availability || !propertyId) return;
    let cancelled = false;
    setLoadingAvailability(true);
    fetchBackend(
      `/${params?.lang}/${params?.companyId}/public/expose/${params?.contactId}/${propertyId}/visit-booking/availability`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propertyId }),
      },
    )
      .then(async (res) => {
        if (!res.ok) throw new Error(`Availability failed: ${res.status}`);
        return (await res.json()) as AvailabilityResponse;
      })
      .then((data) => {
        if (!cancelled) {
          setAvailability(data);
          setLoadingAvailability(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setAvailabilityError(err.message);
          setLoadingAvailability(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    propertyId,
    params?.lang,
    params?.companyId,
    params?.contactId,
    props.availability,
  ]);

  const dayGroups = useMemo(
    () => groupSlotsByDay(availability?.slots ?? []),
    [availability],
  );
  const requestOnly = availability?.fallback === 'requestOnly';

  const slotScrollRef = useRef<HTMLDivElement | null>(null);
  const firstFreeSlotRef = useRef<HTMLButtonElement | null>(null);
  const firstFreeSlotId = useMemo(() => {
    for (const day of dayGroups) {
      for (const slot of day.slots) {
        if (!slot.booked) return slot.id;
      }
    }
    return null;
  }, [dayGroups]);

  // When the slot list has loaded, scroll the INNER slot container to the first
  // bookable (non-greyed) slot so the earliest available appointment is visible.
  // We move only slotScrollRef's own scrollTop — never scrollIntoView, which
  // bubbles to the page and would yank the whole view up to the section heading
  // (especially on mobile, where the page itself scrolls).
  useEffect(() => {
    if (loadingAvailability || !firstFreeSlotId) return;
    // Wait briefly for the slot list to paint before measuring/scrolling.
    const timer = setTimeout(() => {
      const container = slotScrollRef.current;
      const target = firstFreeSlotRef.current;
      if (!container || !target) return;
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      // Leave ~44px so the slot's day-label header stays visible above it.
      const delta = targetRect.top - containerRect.top - 44;
      const nextTop = Math.max(0, container.scrollTop + delta);
      if (Math.abs(nextTop - container.scrollTop) > 4) {
        container.scrollTo({ top: nextTop, behavior: 'smooth' });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [
    firstFreeSlotId,
    loadingAvailability,
  ]);
  const formValid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    /.+@.+\..+/.test(email) &&
    phone.trim().length >= 6 &&
    street.trim().length >= 3 &&
    /^\d{4,5}$/.test(zip.trim()) &&
    city.trim().length >= 2 &&
    !!homeType &&
    consent &&
    (requestOnly || selectedSlot != null);
  const firstNameError =
    (touched.firstName || hasSubmitted) && firstName.trim().length === 0
      ? t('visit_booking_error_required', 'Bitte füllen Sie dieses Feld aus.')
      : null;
  const lastNameError =
    (touched.lastName || hasSubmitted) && lastName.trim().length === 0
      ? t('visit_booking_error_required', 'Bitte füllen Sie dieses Feld aus.')
      : null;
  const emailError =
    (touched.email || hasSubmitted) && !/.+@.+\..+/.test(email)
      ? t(
          'visit_booking_error_email',
          'Bitte geben Sie eine gültige E-Mail Adresse ein.',
        )
      : null;
  const phoneError =
    (touched.phone || hasSubmitted) && phone.trim().length < 6
      ? t(
          'visit_booking_error_phone',
          'Bitte geben Sie eine gültige Telefonnummer ein.',
        )
      : null;
  const streetError =
    (touched.street || hasSubmitted) && street.trim().length < 3
      ? t(
          'visit_booking_error_street',
          'Bitte geben Sie eine gültige Strasse ein.',
        )
      : null;
  const noError = null; // optional, no validation
  const zipError =
    (touched.zip || hasSubmitted) && !/^\d{4,5}$/.test(zip.trim())
      ? t('visit_booking_error_zip', 'Bitte geben Sie eine gültige PLZ ein.')
      : null;
  const cityError =
    (touched.city || hasSubmitted) && city.trim().length < 2
      ? t('visit_booking_error_city', 'Bitte geben Sie einen gültigen Ort ein.')
      : null;
  const homeTypeError =
    (touched.homeType || hasSubmitted) && !homeType
      ? t(
          'visit_booking_error_home_type',
          'Bitte geben Sie Ihre Wohnsituation an.',
        )
      : null;
  const consentError =
    hasSubmitted && !consent
      ? t(
          'visit_booking_error_consent',
          'Bitte stimmen Sie den Bedingungen zu.',
        )
      : null;
  const slotError =
    hasSubmitted && !requestOnly && selectedSlot == null
      ? t('visit_booking_error_slot', 'Bitte wählen Sie einen Termin aus.')
      : null;

  const selectedDateLabel = dayGroups.find((dg) =>
    dg.slots.some((s) => s.id === selectedSlot?.id),
  )?.dateLabel;
  const selectedSlotAdvisor = selectedSlot?.id
    ? availability?.slots?.find((s) => s.id === selectedSlot.id)?.advisor
    : undefined;
  const advisor = selectedSlotAdvisor
    ? { ...(availability?.advisor || {}), ...selectedSlotAdvisor }
    : availability?.advisor;
  const advisorName =
    advisor?.name ||
    t('visit_booking_advisor_fallback_name', 'Ihr Immobilienberater');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setHasSubmitted(true);
    if (!formValid || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    setAlreadyBooked(null);
    // Stable across retries of the *same* booking (same property/slot/email)
    // so the server-side idempotency cache (Redis, TTL 6h) can dedupe a
    // client retry triggered by a proxy timeout instead of double-booking.
    // Must stay deterministic — no Date.now() or other per-call entropy.
    const idempotencyKey = `${propertyId}:${
      selectedSlot?.id ?? 'request'
    }:${email.trim().toLowerCase()}`;
    try {
      const res = await fetchBackend(
        `/${params?.lang}/${params?.companyId}/public/expose/${params?.contactId}/${propertyId}/visit-booking/book`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            propertyId,
            slotId: selectedSlot?.id ?? '',
            slotStart: selectedSlot?.start ?? '',
            slotEnd: selectedSlot?.end ?? '',
            salutation,
            firstName,
            lastName,
            email,
            phone,
            homeType,
            message,
            street,
            no,
            zip,
            city,
            consent,
            idempotencyKey,
            requestOnly,
          }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          code?: string;
          message?: string;
          existingBookingId?: string;
          existingStart?: string | null;
          contactEmail?: string;
        };
        if (body.code === 'ALREADY_BOOKED') {
          setAlreadyBooked({
            existingStart: body.existingStart ?? null,
            contactEmail: body.contactEmail || 'office@agentselly.ch',
          });
          return;
        }
        if (body.code === 'BOOKING_TIMEOUT') {
          // Proxy timed out but the booking may already exist server-side
          // (unified-api can still be finishing the write). Don't invite
          // an immediate retry with a generic failure message.
          throw new Error(
            t(
              'visit_booking_error_timeout',
              'Die Anfrage dauert ungewöhnlich lange. Ihre Buchung wurde möglicherweise bereits angelegt — bitte prüfen Sie Ihr E-Mail-Postfach, bevor Sie es erneut versuchen.',
            ),
          );
        }
        throw new Error(body.message || `Booking failed: ${res.status}`);
      }
      const data = (await res.json()) as {
        status: 'booked' | 'requested';
        slotStart?: string;
        relId?: string;
        propertyId?: string;
        mongoPropertyId?: string;
        contactId?: string;
      };
      setSuccess({
        status: data.status,
        slotStart: data.slotStart,
        dossierId: data.relId || data.propertyId || propertyId,
        mongoPropertyId: data.mongoPropertyId,
        contactId: data.contactId,
      });
      trackEvent('immodossier appointment booked', {
        status: data.status,
      });
    } catch (err) {
      setSubmitError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    // Primary link: ImmoDossier public expose view.
    // contactId comes from the API booking response (newly created for anonymous visitors).
    const cid = success.contactId || params?.contactId || '';
    const viewUrl = `/${params?.lang}/${params?.companyId}/public/expose/${cid}/${propertyId}/view`;
    // Secondary link: Marktplatz (v1 marketplace) — the visitor came from there.
    // NOTE: The marketplace route uses MongoDB _id (agentselly.ch/marktplatz/<id>).
    // If the API returns a mongoPropertyId (different from the route propertyId),
    // use that — otherwise fall back to propertyId from the URL params.
    const mongoId = success.mongoPropertyId || propertyId;
    const marktplatzUrl = `https://www.agentselly.ch/marktplatz/${mongoId}`;
    return (
      <div className="mx-auto w-full max-w-5xl py-6 md:py-8 px-4 sm:px-6">
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center shadow-sm dark:shadow-none">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/60">
            <svg
              className="h-8 w-8 text-emerald-600 dark:text-emerald-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-gray-950 dark:text-zinc-100">
            {success.status === 'booked'
              ? t('visit_booking_success_booked_title', 'Besichtigung gebucht')
              : t(
                  'visit_booking_success_requested_title',
                  'Anfrage übermittelt',
                )}
          </h2>
          <p className="max-w-md text-sm leading-6 text-gray-600 dark:text-zinc-400">
            {success.status === 'booked'
              ? t(
                  'visit_booking_success_booked_body',
                  'Ihre Besichtigung wurde erfolgreich gebucht. Sie erhalten in Kürze eine Bestätigung per E-Mail.',
                )
              : t(
                  'visit_booking_success_requested_body',
                  'Ihre Besichtigungsanfrage wurde übermittelt. Wir melden uns zeitnah bei Ihnen.',
                )}
          </p>
          <div className="mt-4 w-full max-w-sm rounded-xl border border-gray-200 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-950/60 p-5 text-left transition-colors hover:border-gray-300 dark:hover:border-gray-700">
            <a
              href={viewUrl}
              className="group block"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-100 dark:bg-zinc-800 text-gray-900 dark:text-gray-100">
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    {t('visit_booking_success_view_expose', 'Zum Dossier')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">
                    {t(
                      'visit_booking_success_view_expose_hint',
                      'Infos, Bilder & Bewertung Ihrer Traumimmobilie',
                    )}
                  </p>
                </div>
                <svg
                  className="h-5 w-5 shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-900 dark:group-hover:text-gray-100 group-hover:translate-x-0.5 transition-all"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </a>
          </div>
          {/* Legacy: Marktplatz-Link (v1 marketplace — the visitor came from here).
              propertyId = MongoDB _id, z.B. https://www.agentselly.ch/marktplatz/<id>.
              Sobald die neue Website live ist, kann dieser Link auf die neue URL geändert werden. */}
          <div className="mt-3 w-full max-w-sm rounded-xl border border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-3 text-left transition-colors hover:border-gray-300 dark:hover:border-gray-700">
            <a
              href={marktplatzUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400">
                  <svg
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-zinc-200 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                    {t(
                      'visit_booking_success_marktplatz_link',
                      'Zurück zum Marktplatz',
                    )}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {t(
                      'visit_booking_success_marktplatz_hint',
                      'Zurück zur Objekt-Übersicht auf agentselly.ch',
                    )}
                  </p>
                </div>
                <svg
                  className="h-4 w-4 shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
              </div>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl py-4 sm:py-6 md:py-8 px-4 sm:px-6 lg:px-8">
      <form
        className="overflow-hidden bg-white dark:bg-zinc-900 rounded-2xl border border-gray-200 dark:border-zinc-800 shadow-sm dark:shadow-none"
        onSubmit={handleSubmit}
      >
        <div className="grid grid-cols-1 md:grid-cols-[0.9fr_1.1fr]">
          <aside className="vsum border-b border-gray-200 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-950/60 p-4 sm:p-5 md:border-b-0 md:border-r md:p-8">
            <div className="mb-5 md:mb-6 inline-flex items-center gap-2 rounded-full bg-white dark:bg-zinc-900 px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-zinc-800 shadow-sm dark:shadow-none">
              <HomeIcon />{' '}
              {availability?.slots[0]?.address ||
                availability?.address ||
                t('visit_booking_visit_type_badge', 'Vor-Ort-Besichtigung')}
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-gray-950 dark:text-zinc-100 md:text-3xl">
              {t('visit_booking_title', 'Besichtigung buchen')}
            </h2>
            <p className="mt-2 md:mt-3 text-sm leading-6 text-gray-600 dark:text-zinc-400">
              {requestOnly
                ? t(
                    'visit_booking_request_subtitle',
                    'Aktuell sind keine freien Besichtigungstermine online verfügbar. Hinterlassen Sie Ihre Wunschzeit und Kontaktdaten — wir melden uns.',
                  )
                : t(
                    'visit_booking_subtitle',
                    'Wählen Sie einen verfügbaren Termin für die Besichtigung der Immobilie und hinterlassen Sie Ihre Kontaktdaten.',
                  )}
            </p>

            <div className="mt-4 md:mt-6 rounded-xl md:rounded-2xl bg-white dark:bg-zinc-900 p-4 md:p-5 border border-gray-200 dark:border-zinc-800 shadow-sm dark:shadow-none">
              <div className="flex items-center gap-3 md:gap-4">
                {advisor?.photoUrl ? (
                  <img
                    src={advisor.photoUrl}
                    alt={advisorName}
                    className="h-14 w-14 md:h-16 md:w-16 rounded-full object-cover ring-2 ring-gray-100 dark:ring-zinc-800 shadow-sm dark:shadow-none"
                  />
                ) : (
                  <div className="flex h-14 w-14 md:h-16 md:w-16 items-center justify-center rounded-full bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-950 text-lg font-bold uppercase shadow-sm dark:shadow-none">
                    {initials(advisorName)}
                  </div>
                )}
                <div>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-zinc-400">
                    {t('visit_booking_advisor_label', 'Ihre Ansprechperson')}
                  </p>
                  <p className="font-semibold text-gray-950 dark:text-zinc-100">
                    {advisorName}
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-zinc-400">
                    {advisor?.jobTitle ||
                      t('visit_booking_advisor_role', 'Immobilienberatung')}
                  </p>
                </div>
              </div>
              <div className="mt-4 md:mt-5 grid gap-2 md:gap-3 text-sm text-gray-700 dark:text-gray-300">
                <div className="flex items-center gap-3">
                  <CalendarIcon />
                  <span>
                    {availability?.duration
                      ? t(
                          'visit_booking_duration_value',
                          '{duration} Min.',
                          { duration: availability.duration },
                        )
                      : t('visit_booking_duration_fallback', '30–45 Min.')}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <HomeIcon />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {availability?.slots[0]?.address ||
                        availability?.address ||
                        t(
                          'visit_booking_on_site_property',
                          'Vor-Ort-Besichtigung',
                        )}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="p-4 pb-10 sm:p-6 sm:pb-12 md:p-8 space-y-6">
            {isLoggedIn && (
              <div className="rounded-xl border border-gray-200 dark:border-zinc-800 bg-gray-50 dark:bg-zinc-950/60 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                {t('visit_booking_logged_in_as', 'Gebucht als')}{' '}
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {contact?.email}
                </span>
              </div>
            )}

            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <StepBadge active>1</StepBadge>
                <div>
                  <h3 className="font-semibold text-gray-950 dark:text-zinc-100">
                    {t(
                      'visit_booking_choose_slot',
                      'Besichtigungstermin wählen',
                    )}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-zinc-400">
                    {t(
                      'visit_booking_choose_slot_hint',
                      'Alle Zeiten in Schweizer Lokalzeit.',
                    )}
                  </p>
                </div>
              </div>

              {loadingAvailability && (
                <div
                  className="flex flex-col gap-4 py-2"
                  aria-live="polite"
                >
                  {[
                    { slots: 4 },
                    { slots: 3 },
                  ].map((day, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-gray-200 dark:border-zinc-800 p-3 md:p-4 bg-gray-50/50 dark:bg-zinc-950/60"
                    >
                      <div className="mb-3 h-5 w-1/3 rounded-lg bg-gray-200 dark:bg-zinc-800 animate-pulse" />
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                        {Array.from({ length: day.slots }).map((_, sIdx) => (
                          <div
                            key={sIdx}
                            className="h-11 rounded-xl bg-gray-200 dark:bg-zinc-800 animate-pulse"
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {availabilityError && (
                <p className="text-sm text-red-600 dark:text-red-400">
                  {availabilityError}
                </p>
              )}

              {!loadingAvailability &&
                !availabilityError &&
                dayGroups.length === 0 && (
                  <div className="callout rounded-xl border border-gray-200 dark:border-zinc-800 bg-gray-50/70 dark:bg-zinc-950/60 p-4 text-sm text-gray-600 dark:text-zinc-400">
                    <p className="font-semibold text-gray-900 dark:text-zinc-100">
                      {t(
                        'visit_booking_no_slots_title',
                        'Aktuell sind keine freien Besichtigungstermine verfügbar.',
                      )}
                    </p>
                    <p className="mt-1">
                      {t(
                        'visit_booking_no_slots_body',
                        'Hinterlassen Sie Ihre Angaben — wir melden uns zeitnah mit neuen Terminen bei Ihnen.',
                      )}
                    </p>
                  </div>
                )}

              {!loadingAvailability &&
                !availabilityError &&
                dayGroups.length > 0 && (
                  <div className="space-y-3">
                    {dayGroups.length > 1 && (
                      <div
                        className="days flex gap-2 overflow-x-auto pb-1 scrollbar-none"
                        role="group"
                        aria-label={t(
                          'visit_booking_select_day',
                          'Tag auswählen',
                        )}
                      >
                        {dayGroups.map((day) => {
                          const isSelected = selectedSlot
                            ? day.slots.some((s) => s.id === selectedSlot.id)
                            : false;
                          return (
                            <button
                              key={day.dateKey}
                              type="button"
                              onClick={() => {
                                const el = document.getElementById(
                                  `day-${day.dateKey}`,
                                );
                                if (el) {
                                  el.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'nearest',
                                  });
                                }
                              }}
                              aria-pressed={isSelected}
                              className={`day flex-none min-w-[64px] p-2 flex flex-col items-center justify-center rounded-xl border text-center transition-colors cursor-pointer ${
                                isSelected
                                  ? 'active border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-950 font-semibold shadow-sm'
                                  : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                              }`}
                            >
                              <small className="text-[10px] opacity-75">
                                {day.shortWeekday}
                              </small>
                              <b className="text-base leading-tight my-0.5">
                                {day.dayNum}.
                              </b>
                              <small className="text-[10px] opacity-75">
                                {day.shortMonth}
                              </small>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    <div className="relative">
                      <div
                        ref={slotScrollRef}
                        className="flex max-h-[20rem] flex-col gap-3 overflow-y-auto overscroll-contain pr-1 md:max-h-96 md:gap-4 md:pr-2 md:pb-1 md:[mask-image:linear-gradient(to_bottom,black_95%,transparent)] motion-reduce:scroll-auto"
                      >
                        {dayGroups.map((day) => (
                          <div
                            key={day.dateKey}
                            id={`day-${day.dateKey}`}
                            className="rounded-xl border border-gray-200 dark:border-zinc-800 p-3 md:p-4 bg-gray-50/50 dark:bg-zinc-950/60"
                          >
                            <p className="mb-2 md:mb-3 text-sm font-semibold capitalize text-gray-900 dark:text-zinc-100">
                              {day.dateLabel}
                            </p>
                            <div
                              className="slots grid grid-cols-2 gap-2 sm:grid-cols-3"
                              role="group"
                              aria-label={day.dateLabel}
                            >
                              {day.slots.map((slot) => {
                                const isBooked = slot.booked;
                                const active = selectedSlot?.id === slot.id;
                                return (
                                  <button
                                    key={slot.id}
                                    ref={
                                      slot.id === firstFreeSlotId
                                        ? firstFreeSlotRef
                                        : undefined
                                    }
                                    id={`slot-${slot.id}`}
                                    type="button"
                                    disabled={isBooked}
                                    onClick={() => {
                                      setSelectedSlot(slot);
                                      setHasSubmitted(false);
                                    }}
                                    aria-pressed={active}
                                    className={`slot min-h-[44px] rounded-lg border px-2 md:px-3 py-2 text-sm font-semibold transition duration-150 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 ${
                                      isBooked
                                        ? 'border-gray-200 dark:border-zinc-800 bg-gray-100 dark:bg-zinc-800 text-gray-400 dark:text-gray-500 cursor-not-allowed line-through opacity-50'
                                        : active
                                        ? 'active border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-950 shadow-sm'
                                        : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-800 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 shadow-sm dark:shadow-none'
                                    } ${
                                      slotError && !active
                                        ? 'border-red-300 dark:border-red-800'
                                        : ''
                                    }`}
                                  >
                                    {timeLabel(slot.start)}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

              {slotError && (
                <p className="mt-2 text-sm text-red-500">{slotError}</p>
              )}
            </section>

            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <StepBadge active={requestOnly || selectedSlot != null}>
                  2
                </StepBadge>
                <div>
                  <h3 className="font-semibold text-gray-950 dark:text-zinc-100">
                    {t(
                      'visit_booking_contact_title',
                      'Ihre Daten für die Besichtigung',
                    )}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-500 dark:text-zinc-400">
                    {requestOnly
                      ? t(
                          'visit_booking_request_contact_hint',
                          'Wir koordinieren Ihre Besichtigung persönlich.',
                        )
                      : selectedSlot
                      ? selectedSlot.anfahrtStart && selectedSlot.rueckfahrtEnd
                        ? t(
                            'visit_booking_selected_slot_with_travel_hint',
                            'Gewählt: {date}, {time} Uhr (inkl. Reisezeit: {from}–{to} Uhr)',
                            {
                              date: selectedDateLabel,
                              time: timeLabel(selectedSlot.start),
                              from: timeLabel(selectedSlot.anfahrtStart),
                              to: timeLabel(selectedSlot.rueckfahrtEnd),
                            },
                          )
                        : t(
                            'visit_booking_selected_slot_hint_with_date',
                            'Gewählt: {date}, {time} Uhr',
                            {
                              date: selectedDateLabel,
                              time: timeLabel(selectedSlot.start),
                            },
                          )
                      : t(
                          'visit_booking_select_first_hint',
                          'Wählen Sie zuerst einen Besichtigungstermin.',
                        )}
                  </p>
                </div>
              </div>

              <div className="fgrid two grid grid-cols-1 gap-3 md:gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_salutation', 'Anrede')}
                  </label>
                  <div className="flex gap-3 md:gap-4">
                    <label
                      className={`flex min-h-[44px] flex-1 md:flex-none items-center gap-2 rounded-xl border px-3 text-sm cursor-pointer transition-colors ${
                        salutation === '1'
                          ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 font-medium'
                          : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="salutation"
                        checked={salutation === '1'}
                        onChange={() => setSalutation('1')}
                        className="h-4 w-4 text-gray-900 dark:text-white focus-visible:ring-gray-900 dark:focus-visible:ring-white"
                      />
                      {t('visit_booking_salutation_mr', 'Herr')}
                    </label>
                    <label
                      className={`flex min-h-[44px] flex-1 md:flex-none items-center gap-2 rounded-xl border px-3 text-sm cursor-pointer transition-colors ${
                        salutation === '0'
                          ? 'border-gray-900 dark:border-white bg-gray-50 dark:bg-zinc-800 text-gray-900 dark:text-gray-100 font-medium'
                          : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="salutation"
                        checked={salutation === '0'}
                        onChange={() => setSalutation('0')}
                        className="h-4 w-4 text-gray-900 dark:text-white focus-visible:ring-gray-900 dark:focus-visible:ring-white"
                      />
                      {t('visit_booking_salutation_mrs', 'Frau')}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_first_name', 'Vorname')}
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onBlur={() => touch('firstName')}
                    className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                      firstNameError
                        ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                        : 'border-gray-200 dark:border-zinc-800'
                    }`}
                  />
                  {firstNameError && (
                    <p className="mt-1 text-xs text-red-600">
                      {firstNameError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_last_name', 'Nachname')}
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onBlur={() => touch('lastName')}
                    className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                      lastNameError
                        ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                        : 'border-gray-200 dark:border-zinc-800'
                    }`}
                  />
                  {lastNameError && (
                    <p className="mt-1 text-xs text-red-600">{lastNameError}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_email', 'E-Mail')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => touch('email')}
                    disabled={emailLocked}
                    className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 ${
                      emailLocked
                        ? 'border-gray-200 dark:border-zinc-800 bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-gray-400'
                        : emailError
                        ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100'
                        : 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100'
                    }`}
                  />
                  {emailError && (
                    <p className="mt-1 text-xs text-red-600">{emailError}</p>
                  )}
                  {emailLocked && !emailError && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                      {t(
                        'visit_booking_email_locked',
                        'E-Mail aus Ihrem Konto übernommen.',
                      )}
                    </p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_phone', 'Telefon')}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    onBlur={() => touch('phone')}
                    className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                      phoneError
                        ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                        : 'border-gray-200 dark:border-zinc-800'
                    }`}
                  />
                  {phoneError && (
                    <p className="mt-1 text-xs text-red-600">{phoneError}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_address', 'Adresse')}
                  </label>
                  <div className="grid grid-cols-1 gap-3 md:gap-4 md:grid-cols-4">
                    <div className="md:col-span-3">
                      <label
                        className="sr-only"
                        htmlFor="street"
                      >
                        {t('visit_booking_street', 'Strasse')}
                      </label>
                      <input
                        id="street"
                        type="text"
                        value={street}
                        onChange={(e) => setStreet(e.target.value)}
                        onBlur={() => touch('street')}
                        placeholder={t('visit_booking_street', 'Strasse')}
                        className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                          streetError
                            ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                            : 'border-gray-200 dark:border-zinc-800'
                        }`}
                      />
                      {streetError && (
                        <p className="mt-1 text-xs text-red-600">
                          {streetError}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        className="sr-only"
                        htmlFor="no"
                      >
                        {t('visit_booking_no', 'Nr.')}
                      </label>
                      <input
                        id="no"
                        type="text"
                        value={no}
                        onChange={(e) => setNo(e.target.value)}
                        onBlur={() => touch('no')}
                        placeholder={t('visit_booking_no', 'Nr.')}
                        className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                          noError
                            ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                            : 'border-gray-200 dark:border-zinc-800'
                        }`}
                      />
                      {noError && (
                        <p className="mt-1 text-xs text-red-600">{noError}</p>
                      )}
                    </div>
                    <div className="md:col-span-1">
                      <label
                        className="sr-only"
                        htmlFor="zip"
                      >
                        {t('visit_booking_zip', 'PLZ')}
                      </label>
                      <input
                        id="zip"
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        value={zip}
                        onChange={(e) => setZip(e.target.value)}
                        onBlur={() => touch('zip')}
                        placeholder={t('visit_booking_zip', 'PLZ')}
                        className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                          zipError
                            ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                            : 'border-gray-200 dark:border-zinc-800'
                        }`}
                      />
                      {zipError && (
                        <p className="mt-1 text-xs text-red-600">{zipError}</p>
                      )}
                    </div>
                    <div className="md:col-span-3">
                      <label
                        className="sr-only"
                        htmlFor="city"
                      >
                        {t('visit_booking_city', 'Ort')}
                      </label>
                      <input
                        id="city"
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        onBlur={() => touch('city')}
                        placeholder={t('visit_booking_city', 'Ort')}
                        className={`min-h-[44px] w-full rounded-xl border px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500 ${
                          cityError
                            ? 'border-red-300 dark:border-red-800 focus-visible:border-red-500 focus-visible:ring-red-500/20'
                            : 'border-gray-200 dark:border-zinc-800'
                        }`}
                      />
                      {cityError && (
                        <p className="mt-1 text-xs text-red-600">{cityError}</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_home_type', 'Wohnsituation')}
                  </label>
                  <div className="flex flex-wrap gap-3 md:gap-4">
                    <HomeTypeRadio
                      value="owning"
                      selected={homeType === 'owning'}
                      onChange={setHomeType}
                      label={t(
                        'visit_booking_home_type_owning',
                        'Ich wohne in meinem Eigenheim',
                      )}
                    />
                    <HomeTypeRadio
                      value="renting"
                      selected={homeType === 'renting'}
                      onChange={setHomeType}
                      label={t(
                        'visit_booking_home_type_renting',
                        'Ich wohne in einem Mietobjekt',
                      )}
                    />
                  </div>
                  {homeTypeError && (
                    <p className="mt-1 text-xs text-red-600">{homeTypeError}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('visit_booking_message', 'Nachricht (optional)')}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    className="w-full rounded-xl border border-gray-200 dark:border-zinc-800 px-3 py-2 text-sm transition focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-900 dark:focus-visible:ring-gray-100 bg-white dark:bg-zinc-900 text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>

              <label className="mt-5 flex items-start gap-3 text-sm text-gray-600 dark:text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className={`mt-1 h-4 w-4 rounded border-gray-300 dark:border-zinc-800 text-gray-900 dark:text-white focus-visible:ring-gray-900 dark:focus-visible:ring-white ${
                    consentError
                      ? 'ring-2 ring-red-400 ring-offset-2 border-red-300'
                      : ''
                  }`}
                />
                <span>
                  {t('visit_booking_consent_prefix', 'Ich habe die ')}
                  <a
                    href="https://www.agentselly.ch/datenschutz/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {t(
                      'visit_booking_consent_privacy_link',
                      'Datenschutzerklärung',
                    )}
                  </a>
                  {t(
                    'visit_booking_consent_suffix',
                    ' gelesen und stimme dieser zu.',
                  )}
                </span>
              </label>

              {consentError && (
                <p className="mt-1 text-xs text-red-500">{consentError}</p>
              )}

              {alreadyBooked && (
                <div
                  role="alert"
                  className="mt-4 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-950/20 p-4 text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200">
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0 text-sm">
                      <h3 className="font-semibold text-amber-900 dark:text-amber-200">
                        {t(
                          'visit_booking_already_booked_title',
                          'Besichtigungstermin bereits vorhanden',
                        )}
                      </h3>
                      <p className="mt-1 text-amber-800 dark:text-amber-300">
                        {t(
                          'visit_booking_already_booked_body',
                          'Für diese Immobilie besteht für Sie bereits ein Besichtigungstermin.',
                        )}
                      </p>
                      {alreadyBooked.existingStart &&
                        formatDateTimeLong(alreadyBooked.existingStart) && (
                          <p className="mt-2 font-medium text-amber-950 dark:text-amber-100">
                            {t(
                              'visit_booking_already_booked_date_label',
                              'Bestehender Termin: ',
                            )}
                            <span>
                              {formatDateTimeLong(alreadyBooked.existingStart)}{' '}
                              Uhr
                            </span>
                          </p>
                        )}
                      <p className="mt-2 text-xs text-amber-800 dark:text-amber-300">
                        {t(
                          'visit_booking_already_booked_rebook_prefix',
                          'Möchten Sie Ihren Termin umbuchen oder stornieren? Bitte wenden Sie sich an ',
                        )}
                        <a
                          href={`mailto:${
                            alreadyBooked.contactEmail || 'office@agentselly.ch'
                          }`}
                          className="font-medium underline hover:text-amber-950 dark:hover:text-amber-100 transition-colors"
                        >
                          {alreadyBooked.contactEmail || 'office@agentselly.ch'}
                        </a>
                        {t('visit_booking_already_booked_rebook_suffix', '.')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitError && (
                <p className="mt-4 text-sm text-red-600 dark:text-red-400">
                  {submitError}
                </p>
              )}

              <div className="mt-6">
                <Button
                  variant="primary"
                  showArrow
                  isLoading={submitting}
                  disabled={submitting}
                >
                  {submitting
                    ? t('visit_booking_submitting', 'Wird gebucht…')
                    : requestOnly
                    ? t(
                        'visit_booking_submit_request',
                        'Besichtigungsanfrage senden',
                      )
                    : t('visit_booking_submit', 'Besichtigung bestätigen')}
                </Button>
              </div>
            </section>
          </div>
        </div>
      </form>
    </div>
  );
}
